import React, { FC, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BoundaryApprovalJob } from '../domain/BoundaryApprovalJob';
import { alertSuccess } from '../../alertDispatcher';

interface BoundaryApprovalSidebarProps {
  jobs: BoundaryApprovalJob[];
  activeJobId: string | null;
  isLoading: boolean;
  onRefresh: () => Promise<void>;
  onJobClick: (jobId: string) => Promise<void>;
  onJobVisibilityToggle: (jobId: string) => void;
  onApprove: (jobId: string) => Promise<void>;
  onJobFocus: (jobId: string) => void;
  hasUnsavedEdits: boolean;
  highlightedJobId: string | null;
}

export const BoundaryApprovalSidebar: FC<BoundaryApprovalSidebarProps> = ({
  jobs,
  activeJobId,
  isLoading,
  onRefresh,
  onJobClick,
  onJobVisibilityToggle,
  onApprove,
  onJobFocus,
  hasUnsavedEdits,
  highlightedJobId,
}) => {
  const listItemRefs = useRef<{ [jobId: string]: HTMLLIElement | null }>({});

  const handleRefresh = useCallback(async () => {
    await onRefresh();
  }, [onRefresh]);

  const handleJobClick = useCallback(
    async (jobId: string) => {
      await onJobClick(jobId);
    },
    [onJobClick],
  );

  const handleJobFocus = useCallback(
    (jobId: string, event: React.MouseEvent) => {
      event.stopPropagation();

      // Copy job name to clipboard
      const job = jobs.find((j) => j.id === jobId);

      if (job) {
        navigator.clipboard
          .writeText(job.name)
          .then(() => {
            alertSuccess(`Copied "${job.name}" to clipboard`);
          })
          .catch((err) => {
            console.error('Failed to copy to clipboard:', err);
          });
      }

      onJobFocus(jobId);
    },
    [onJobFocus, jobs],
  );

  const handleApprove = useCallback(
    async (jobId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      await onApprove(jobId);
    },
    [onApprove],
  );

  const handleVisibilityToggle = useCallback(
    (jobId: string, event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      onJobVisibilityToggle(jobId);
    },
    [onJobVisibilityToggle],
  );

  const getBackgroundColor = (job: BoundaryApprovalJob): string => {
    if (job.id === highlightedJobId) {
      return '#e1bee7'; // purple
    }

    if (job.isApproved) {
      return '#c8e6c9'; // light green
    }

    if (job.isActive) {
      return '#ffe0b2'; // orange
    }

    return 'transparent';
  };

  // Auto-scroll to highlighted job
  useEffect(() => {
    if (highlightedJobId && listItemRefs.current[highlightedJobId]) {
      listItemRefs.current[highlightedJobId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [highlightedJobId]);

  return (
    <Paper
      elevation={3}
      sx={{
        width: 350,
        height: 'calc(100vh - 40px)', // Account for app bar height
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 40, // Start below the app bar
        zIndex: 1, // Lower z-index so map UI elements (like Poppers with z-index 2) appear on top
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Boundary Approval
        </Typography>

        <Button
          variant="outlined"
          startIcon={isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
          fullWidth
          onClick={handleRefresh}
          disabled={isLoading || hasUnsavedEdits}
        >
          Refresh Jobs
        </Button>
        {hasUnsavedEdits && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
            Save changes before refreshing (or load another job)
          </Typography>
        )}
      </Box>

      <List sx={{ flex: 1, overflow: 'auto', overscrollBehavior: 'contain' }}>
        {jobs.length === 0 && !isLoading && (
          <ListItem>
            <ListItemText primary="No jobs to approve" secondary="Click refresh to load jobs" />
          </ListItem>
        )}

        {jobs.map((job) => (
          <ListItem
            key={job.id}
            ref={(el) => (listItemRefs.current[job.id] = el)}
            disablePadding
            sx={{
              backgroundColor: getBackgroundColor(job),
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              transition: 'background-color 0.3s ease',
            }}
          >
            <Box sx={{ display: 'flex', width: '100%' }}>
              <ListItemIcon sx={{ minWidth: 'auto', pl: 1, alignSelf: 'flex-start', pt: 1 }}>
                <Checkbox
                  edge="start"
                  checked={job.isVisible}
                  onChange={(e) => handleVisibilityToggle(job.id, e)}
                  tabIndex={-1}
                  disableRipple
                  onClick={(e) => e.stopPropagation()}
                  disabled={job.isActive}
                />
              </ListItemIcon>

              <ListItemButton onClick={(e) => handleJobFocus(job.id, e)} dense sx={{ pl: 1, flex: 1 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {job.name}
                      {job.isApproved && <CheckCircleIcon fontSize="small" color="success" />}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {job.isActive && <span>Active</span>}
                      <span>Operator: {job.operator}</span>
                      <span>Sample Date: {job.sampleDate}</span>
                      {job.boundaryChangeNotes && (
                        <span style={{ fontStyle: 'italic' }}>Notes: {job.boundaryChangeNotes}</span>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            </Box>

            {/* Action buttons row */}
            <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 1, justifyContent: 'flex-start' }}>
              {!job.isApproved && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={(e) => handleJobClick(job.id)}
                  disabled={job.isActive}
                >
                  {job.isActive ? 'Loaded' : 'Load'}
                </Button>
              )}

              {job.isActive && !job.isApproved && (
                <Button variant="contained" color="primary" size="small" onClick={(e) => handleApprove(job.id, e)}>
                  Approve
                </Button>
              )}
            </Box>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};
